// src/pages/quality/AssessmentQualityPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import CreateCallAssessmentForm from '../../components/quality/CreateCallAssessmentForm';
import CallAssessmentForm from '../../components/quality/CallAssessmentForm';
import AssessmentCard from '../../components/quality/AssessmentCard';
import TicketsCountForm from '../../components/quality/TicketsCountForm';
import api from '../../services/api';

import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Pagination from "../../components/Pagination";

const AssessmentQualityPage = () => {
  const { projects, isAnalyticsView, roleNames } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    // Если у пользователя только один проект, выбираем его автоматически
    if (projects && projects.length === 1) {
      return projects[0].id;
    }
    // Иначе пытаемся восстановить из localStorage
    const saved = localStorage.getItem('assessment_project_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [showForm, setShowForm] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState({
    total_score_less_10_count: 0,
    feedback_count: 0
  });
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [fieldOptions, setFieldOptions] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showTicketsForm, setShowTicketsForm] = useState(false);
  const [filters, setFilters] = useState({
    agent_name: '',
    has_feedback: '',
    assessment_type: '',
    service: '',
    receipt_channel: '',
    srq: '',
    impact: '',
    group: '',
    sound_id: ''
  });
  const [isSyncingFilters, setIsSyncingFilters] = useState(false); // Флаг для предотвращения повторного запроса при синхронизации
  const [isFetching, setIsFetching] = useState(false); // Флаг для блокировки повторных запросов
  const [tempFilterValues, setTempFilterValues] = useState({
    sound_id: '',
    srq: ''
  }); // Временные значения для текстовых полей поиска
  
  // Получаем операторов из fieldOptions
  const operators = fieldOptions.operators || [];
  
  // Получаем уникальные сервисы из оценок
  const uniqueServices = Array.from(
    new Set(
      assessments
        .map(assessment => assessment.service)
        .filter(service => service && service.trim() !== '')
    )
  ).sort();
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;
  const navigate = useNavigate();

  useEffect(() => {
    // Авторизация уже проверена через ProtectedRoute
    setLoading(false);
  }, [navigate]);

  // Функция для обновления оценок
  const fetchAssessments = async (page = 1) => {
    // Прерываем выполнение, если идет синхронизация фильтров или уже выполняется запрос
    if (isSyncingFilters || isFetching) {
      console.log('Пропуск загрузки данных: идет синхронизация фильтров или уже выполняется запрос');
      return;
    }
    
    // Устанавливаем флаги
    setIsFetching(true);
    setAssessmentsLoading(true);
    try {
      // Формируем URL с фильтрами
      const params = new URLSearchParams({
        project_id: selectedProjectId,
        page: page,
        per_page: 30
      });
      
      // Добавляем фильтры дат
      if (dateFilters.date_filter_from) {
        params.append('date_filter_from', toBackendFormat(dateFilters.date_filter_from));
      }
      if (dateFilters.date_filter_to) {
        params.append('date_filter_to', toBackendFormat(dateFilters.date_filter_to));
      }
      
      // Добавляем другие фильтры
      if (filters.sound_id) {
        params.append('sound_id', filters.sound_id);
      }
      if (filters.agent_name) {
        params.append('agent_name', filters.agent_name);
      }
      if (filters.has_feedback !== '') {
        params.append('has_feedback', filters.has_feedback);
        // При фильтрации по наличию ОС, добавляем фильтр по total_score < 1.0
        if (filters.has_feedback === 'true') {
          params.append('score_filter', 'less_than_100');
        }
      }
      if (filters.assessment_type) {
        params.append('assessment_type', filters.assessment_type);
      }
      if (filters.service) {
        params.append('service', filters.service);
      }
      if (filters.receipt_channel) {
        params.append('receipt_channel', filters.receipt_channel);
      }
      if (filters.srq) {
        params.append('srq', filters.srq);
      }
      if (filters.impact) {
        params.append('impact', filters.impact);
      }
      if (filters.group) {
        params.append('group', filters.group);
      }
      
      const response = await api.get(`/quality/assessments?${params.toString()}`);
      
      // Проверяем, есть ли данные с пагинацией
      if (response.data.page) {
        setAssessments(response.data.data || []);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.pages);
      } else {
        // Без пагинации
        setAssessments(response.data.data || []);
        setCurrentPage(1);
        setTotalPages(1);
      }
      
      // Обновляем статистику из фильтров
      if (response.data.filters) {
        setStats({
          total_score_less_10_count: response.data.filters.total_score_less_10_count || 0,
          feedback_count: response.data.filters.feedback_count || 0
        });
      }
      
      // Устанавливаем флаг синхронизации ВСЕХ фильтров
      setIsSyncingFilters(true);
      
      // Обновляем фильтры дат из ответа сервера, если они отличаются
      if (response.data.filters) {
        const serverFrom = response.data.filters.date_filter_from || getDefaultDateFrom();
        const serverTo = response.data.filters.date_filter_to || getDefaultDateTo();
        
        const inputFrom = toInputFormat(serverFrom);
        const inputTo = toInputFormat(serverTo);
        
        if (inputFrom !== dateFilters.date_filter_from) {
          handleDateBlur({target: {name: 'date_filter_from', value: inputFrom}});
        }
        if (inputTo !== dateFilters.date_filter_to) {
          handleDateBlur({target: {name: 'date_filter_to', value: inputTo}});
        }
      }
      
      // Сбрасываем флаг после обновления ВСЕХ фильтров
      setTimeout(() => setIsSyncingFilters(false), 0);
    } catch (err) {
      console.error('Ошибка загрузки оценок:', err);
    } finally {
      setAssessmentsLoading(false);
      setIsFetching(false);
    }
  };

  // Загрузка оценок при выборе проекта или изменении фильтров
  useEffect(() => {
    // Прерываем выполнение, если идет синхронизация фильтров или уже выполняется запрос
    if (isSyncingFilters || isFetching) {
      console.log('Пропуск загрузки данных: идет синхронизация фильтров или уже выполняется запрос');
      return;
    }
    
    if (!selectedProjectId) {
      setAssessments([]);
      setCurrentPage(1);
      setTotalPages(1);
      return;
    }

    fetchAssessments(1);
  }, [selectedProjectId, filters, dateFilters.date_filter_from, dateFilters.date_filter_to]);

  // Загрузка опций полей и операторов при выборе проекта
  useEffect(() => {
    if (!selectedProjectId) {
      setFieldOptions({});
      return;
    }
    
    const fetchAllData = async () => {
      try {
        // Загружаем опции полей
        const optionsResponse = await api.get(`/field-options/all?project_id=${selectedProjectId}`);
        let fieldOptionsData = {};
        
        if (optionsResponse.data.success) {
          fieldOptionsData = optionsResponse.data.data || {};
        }
        
        // Загружаем операторов
        const operatorsResponse = await api.get(`/operators?project_id=${selectedProjectId}`);
        let operatorsList = operatorsResponse.data.operators || [];
        
        // Проверяем, есть ли в assessments agent_name, которых нет в operatorsList
        const uniqueAgentNames = [...new Set(assessments.map(a => a.agent_name).filter(Boolean))];
        
        uniqueAgentNames.forEach(agentName => {
          // Проверяем, есть ли уже такой оператор в списке
          const exists = operatorsList.some(op => 
            `${op.surname} ${op.name}` === agentName
          );
          
          if (!exists) {
            // Разделяем имя на фамилию и имя
            const nameParts = agentName.trim().split(' ');
            if (nameParts.length >= 2) {
              const surname = nameParts[0];
              const name = nameParts[1];
              
              operatorsList.push({
                id: -1, // временный ID для отсутствующих в базе операторов
                surname: surname,
                name: name
              });
            }
          }
        });
        
        // Объединяем данные и устанавливаем в состояние
        setFieldOptions(prev => ({
          ...fieldOptionsData,
          operators: operatorsList
        }));
        
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
      }
    };
    
    fetchAllData();
  }, [selectedProjectId]);
  
  const handleProjectSelect = (projectId) => {
    setSelectedProjectId(projectId);
    // Сохраняем в localStorage
    if (projectId) {
      localStorage.setItem('assessment_project_id', projectId);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    // Для текстовых полей поиска обновляем только временные значения
    if (name === 'sound_id' || name === 'srq') {
      setTempFilterValues(prev => ({ ...prev, [name]: value }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFilterBlur = (e) => {
    const { name, value } = e.target;
    // При потере фокуса применяем временное значение
    if (name === 'sound_id' || name === 'srq') {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFilterKeyDown = (e) => {
    if (e.key === 'Enter') {
      const { name, value } = e.target;
      // При нажатии Enter применяем временное значение
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
      e.target.blur();
    }
  };

  const handleOpenForm = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    // Сбрасываем выбранную оценку при закрытии формы
    setSelectedAssessment(null);
    // Не сбрасываем выбранный проект — он сохраняется в localStorage
  };

  const handleOpenTicketsForm = () => {
    setShowTicketsForm(true);
  };

  const handleCloseTicketsForm = () => {
    setShowTicketsForm(false);
  };

  const handleTicketsCountSaved = () => {
    // Можно добавить логику после сохранения
    handleCloseTicketsForm();
  };

  const handleAssessmentDeleted = () => {
    // Обновляем список оценок
    fetchAssessments(currentPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAssessments(page);
  };
  if (loading) {
    return (
        <LoadingSpinner text="Загрузка новостей..." />
      // <div className="loading-container">
      //   <div className="spinner">●●●</div>
      //   <div>Загрузка проектов...</div>
      // </div>
    );
  }

  return (
    <div className="main-content">
      <h1>Оценка качества</h1>

      {projects.length === 0 ? (
        <div className="no-projects">
          У вас нет доступных проектов.
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="projectSelect">Выберите проект:</label>
          <select
            id="projectSelect"
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="project-select"
          >
            <option value="">-- Выберите проект --</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name_text}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {selectedProjectId && !roleNames.has('operator') && (
          <div className="stats-container" style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            <div style={{
              flex: '1',
              padding: '15px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {stats.total_score_less_10_count}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                Оценок по которым необходима ОС
              </div>
            </div>
            <div style={{
              flex: '1',
              padding: '15px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {stats.feedback_count}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                Оценок с ОС
              </div>
            </div>
          </div>
          )}
          {selectedProjectId && (
          <div className="filters-container">
          <div className="date-filters" style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label htmlFor="date_filter_from" className="form-label">Дата от</label>
              <input
                type="datetime-local"
                id="date_filter_from"
                name="date_filter_from"
                className="form-control"
                value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
                onChange={handleDateChange}
                onBlur={handleDateBlur}
                step="1"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label htmlFor="date_filter_to" className="form-label">Дата до</label>
              <input
                type="datetime-local"
                id="date_filter_to"
                name="date_filter_to"
                className="form-control"
                value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
                onChange={handleDateChange}
                onBlur={handleDateBlur}
                step="1"
                style={{ width: '100%' }}
              />
            </div>
          </div>
          
          <div className="other-filters" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="filter-group" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              { !roleNames.has('operator') &&
                  (<div style={{flex: 1, minWidth: '200px'}}>
                    <label htmlFor="agent_name_filter">Имя оператора:</label>
                    <select
                        id="agent_name_filter"
                        name="agent_name"
                        value={filters.agent_name}
                        onChange={handleFilterChange}
                        className="filter-select"
                        style={{width: '100%'}}
                    >
                      <option value="">Все</option>
                      <option value="no_operator">Нет оператора</option>
                      {operators.map((operator) => (
                          <option key={operator.id} value={`${operator.surname} ${operator.name}`}>
                            {`${operator.surname} ${operator.name}`}
                          </option>
                      ))}
                    </select>
                  </div>)
              }
              <div style={{flex: 1, minWidth: '150px'}}>
                <label htmlFor="has_feedback_filter">ОС:</label>
                <select
                    id="has_feedback_filter"
                    name="has_feedback"
                    value={filters.has_feedback}
                    onChange={handleFilterChange}
                    className="filter-select"
                    style={{width: '100%'}}
                >
                  <option value="">Все</option>
                  <option value="true">Да</option>
                  <option value="false">Нет</option>
                </select>
              </div>

              <div style={{flex: 1, minWidth: '150px'}}>
                <label htmlFor="assessment_type_filter">Тип оценки:</label>
                <select
                  id="assessment_type_filter"
                  name="assessment_type"
                  value={filters.assessment_type}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="no_assessment_type">Нет типа</option>
                  {(fieldOptions.assessment_type || []).map((option) => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="service_filter">Сервис:</label>
                <select
                  id="service_filter"
                  name="service"
                  value={filters.service}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="no_service">Нет сервиса</option>
                  {uniqueServices.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="receipt_channel_filter">Канал поступления:</label>
                <select
                  id="receipt_channel_filter"
                  name="receipt_channel"
                  value={filters.receipt_channel}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="no_receipt_channel">Нет канала</option>
                  {(fieldOptions.receipt_channel || []).map((option) => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="impact_filter">Impact:</label>
                <select
                  id="impact_filter"
                  name="impact"
                  value={filters.impact}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="no_impact">Нет impact</option>
                  {(fieldOptions.impact || []).map((option) => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="group_filter">Group:</label>
                <select
                  id="group_filter"
                  name="group"
                  value={filters.group}
                  onChange={handleFilterChange}
                  className="filter-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Все</option>
                  <option value="no_group">Нет group</option>
                  {(fieldOptions.group || []).map((option) => (
                    <option key={option.id} value={option.option_value}>
                      {option.option_text}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="sound_id_filter">ID звонка:</label>
                <input
                  id="sound_id_filter"
                  name="sound_id"
                  value={tempFilterValues.sound_id}
                  onChange={handleFilterChange}
                  onBlur={handleFilterBlur}
                  onKeyDown={handleFilterKeyDown}
                  className="form-control"
                  style={{ width: '100%' }}
                  placeholder="Поиск по ID звонка..."
                />
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="srq_filter">SRQ:</label>
                <input
                  id="srq_filter"
                  name="srq"
                  value={tempFilterValues.srq}
                  onChange={handleFilterChange}
                  onBlur={handleFilterBlur}
                  onKeyDown={handleFilterKeyDown}
                  className="form-control"
                  style={{ width: '100%' }}
                  placeholder="Поиск по SRQ..."
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProjectId && isAnalyticsView && (
        <div className="project-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <button
              className="btn-select-project"
              onClick={handleOpenForm}
            >
              Оценить
            </button>
          </div>
          <div>
            <button
              className="btn-select-project"
              onClick={handleOpenTicketsForm}
            >
              Количество обращений по дням
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно формы оценки звонка */}
      {showForm && selectedProjectId && (
        <div className="modal-overlay">
          <div className="modal-content wide-modal">
            {selectedAssessment ? (
              <CallAssessmentForm
                projectId={selectedProjectId}
                onClose={handleCloseForm}
                onAssessmentUpdated={fetchAssessments}
                assessment={selectedAssessment}
              />
            ) : (
              <CreateCallAssessmentForm
                projectId={selectedProjectId}
                onClose={handleCloseForm}
                onAssessmentCreated={fetchAssessments}
              />
            )}
          </div>
        </div>
      )}

      {/* Модальное окно формы количества обращений */}
      {showTicketsForm && selectedProjectId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <TicketsCountForm
              projectId={selectedProjectId}
              onClose={handleCloseTicketsForm}
              onTicketsCountSaved={handleTicketsCountSaved}
            />
          </div>
        </div>
      )}

      {/* Список оценок */}
      {selectedProjectId && (
        <div className="assessments-list">
          <h2>История оценок</h2>
          {assessmentsLoading ? (
            <div className="loading">Загрузка оценок...</div>
          ) : assessments.length === 0 ? (
            <div className="no-data">Нет оценок для выбранного проекта</div>
          ) : (
            <>
              <div className="cards-container">
                {assessments.map((assessment) => (
                  <AssessmentCard
                    key={assessment.id}
                    assessment={assessment}
                    projectSelect={selectedProjectId}
                    onEdit={(assessment) => {
                      setSelectedAssessment(assessment);
                      setShowForm(true);
                    }}
                    onAssessmentDeleted={handleAssessmentDeleted}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AssessmentQualityPage;